import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileDown, Check, UtensilsCrossed } from "lucide-react";

export default function LeadMagnet() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const triggerDownload = () => {
    const link = document.createElement("a");
    link.href = "/meniu-elysium-events.pdf";
    link.download = "Meniu-Elysium-Events.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        source: "meniu-pdf"
      });
      triggerDownload();
      setDone(true);
      toast.success("Mulțumim! Descărcarea meniului a început.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "A apărut o eroare. Te rugăm să încerci din nou.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="lead-magnet"
      className="relative bg-[#0A0A0A] py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 80% 20%, #D4AF37 0%, transparent 45%)" }} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center bg-gradient-to-br from-[#141414] to-[#0d0d0d] border border-[#D4AF37]/20 p-8 sm:p-12 lg:p-16">
          {/* Left: pitch */}
          <div className="space-y-6">
            <span className="text-[10px] font-semibold tracking-[0.3em] text-[#D4AF37] uppercase flex items-center gap-2">
              <UtensilsCrossed size={13} /> Meniu Premium Gratuit
            </span>
            <h2 className="font-serif-lux text-3xl sm:text-4xl lg:text-5xl text-white font-medium leading-tight">
              Descarcă meniul nostru de nuntă
            </h2>
            <p className="text-white/60 font-light leading-relaxed">
              Completează formularul și îți trimitem instant meniul complet Elysium Events — preparate rafinate,
              opțiuni de open bar și pachetele noastre premium. Perfect pentru a-ți planifica evenimentul de vis.
            </p>
            <ul className="space-y-3">
              {["Preparate gourmet & aperitive fine", "Opțiuni de open bar și candy bar", "Pachete complete personalizabile"].map((item) => (
                <li key={item} className="flex items-center gap-3 text-sm text-white/70">
                  <span className="p-1 bg-[#D4AF37]/15 text-[#D4AF37] rounded-full"><Check size={13} /></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Right: form / success */}
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 p-8">
            {done ? (
              <div className="text-center py-8 space-y-4" data-testid="lead-magnet-success">
                <div className="mx-auto w-16 h-16 flex items-center justify-center bg-[#D4AF37]/15 text-[#D4AF37] rounded-full">
                  <Check size={30} />
                </div>
                <h3 className="font-serif-lux text-2xl text-white">Meniul se descarcă!</h3>
                <p className="text-sm text-white/60">Dacă descărcarea nu a pornit automat, apasă butonul de mai jos.</p>
                <button
                  onClick={triggerDownload}
                  data-testid="lead-magnet-redownload"
                  className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C5A880] text-black text-xs font-semibold tracking-widest uppercase px-6 py-3 transition-colors"
                >
                  <FileDown size={14} /> Descarcă din nou
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="lead-magnet-form">
                <div className="space-y-2">
                  <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Nume Complet</label>
                  <input
                    type="text" name="name" required
                    data-testid="lead-input-name"
                    value={form.name} onChange={handleChange}
                    placeholder="Popescu Andrei"
                    className="w-full bg-[#121212] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Email</label>
                  <input
                    type="email" name="email" required
                    data-testid="lead-input-email"
                    value={form.email} onChange={handleChange}
                    placeholder="andrei@example.com"
                    className="w-full bg-[#121212] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Telefon</label>
                  <input
                    type="tel" name="phone" required
                    data-testid="lead-input-phone"
                    value={form.phone} onChange={handleChange}
                    placeholder="0720 000 000"
                    className="w-full bg-[#121212] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                  />
                </div>
                <button
                  type="submit" disabled={submitting}
                  data-testid="lead-magnet-submit"
                  className="w-full bg-[#D4AF37] hover:bg-[#C5A880] text-black text-xs font-semibold tracking-widest uppercase py-4 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? (
                    <span className="animate-pulse">Se pregătește...</span>
                  ) : (
                    <>
                      <FileDown size={15} />
                      <span>Descarcă meniul PDF</span>
                    </>
                  )}
                </button>
                <p className="text-[11px] text-white/40 text-center">Datele tale sunt în siguranță. Fără spam.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
